/**
 * @fileoverview User details query hook.
 * @description Wraps GET /users/{userId} for the View and Edit User dialogs. The result carries
 * the ETag Update User needs as its If-Match.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import { getUserDetail } from '@/features/users/services/users.service'

import type { VersionedUserDetail } from '@/features/users/types/users.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Fetches one user's details. Idle while userId is empty.
 * @param {string} userId - The user's id, or '' for none.
 * @returns {import('@tanstack/react-query').UseQueryResult<VersionedUserDetail, ApiError>} The
 * query result.
 */
export function useUserDetail(userId: string) {
  return useQuery<VersionedUserDetail, ApiError>({
    queryKey: USERS_QUERY_KEYS.detail(userId),
    queryFn: () => getUserDetail(userId),
    enabled: userId !== '',
  })
}
